import {
  Edit,
  Form,
  Image,
  Input,
  InputNumber,
  Select,
  Switch,
  Table,
  Tabs,
  TextField,
  Upload,
  UploadFile,
  UploadProps,
  useForm,
  useSelect,
  useTable,
} from "@pankod/refine-antd";
import { IResourceComponentsProps, useShow } from "@pankod/refine-core";
import routerProvider from "@pankod/refine-react-router-v6";
import { PayoutsOverviewList } from "pages/adminDashboard/payouts/list";
import { PayoutsOverviewListPrintOut } from "pages/adminDashboard/payoutsPrintOut/list";
import { useCallback, useEffect, useMemo, useState } from "react";
import "react-mde/lib/styles/css/react-mde-all.css";
import { ILabel } from "types/label.interface";
import { IUser, IUserResponse } from "types/user.interface";
import { beforeUpload } from "utils";
import "./styles.scss";

export const LabelEdit: React.FC<IResourceComponentsProps> = () => {
  const [fileListSquare, setFileListSquare] = useState<UploadFile[]>();
  const [fileListBanner, setFileListBanner] = useState<UploadFile[]>();
  const [minPrice, setMinPrice] = useState<number>(0);
  const [maxPrice, setMaxPrice] = useState<number>(0);
  const [minUpload, setMinUpload] = useState<number>(0);
  const [maxUpload, setMaxUpload] = useState<number>(0);
  const [tabIndex, setTabIndex] = useState<number>(1);

  const { Link } = routerProvider;

  const { queryResult } = useShow<ILabel>();
  const { data, isLoading } = queryResult;
  const record: any = data?.data;

  const [isContributorPricing, setIsContributorPricing] = useState<boolean>();

  const { formProps, saveButtonProps, formLoading } = useForm<ILabel>();

  const { selectProps: UserSelectProps } = useSelect<IUser>({
    resource: "users/contributor",
    optionLabel: "username",
    optionValue: "_id",
  });

  const { tableProps, searchFormProps } = useTable<IUserResponse>({
    resource: `users/contributor`,
  });

  const { TabPane } = Tabs;

  useEffect(() => {
    if (record?.data.squareImageUrl) {
      setFileListSquare([
        { uid: "1", name: "square", url: record?.data.squareImageUrl },
      ]);
    }
    if (record?.data.bannerImageUrl) {
      setFileListBanner([
        { uid: "1", name: "banner", url: record?.data.bannerImageUrl },
      ]);
    }

    setIsContributorPricing(record?.data.contributorPricing);
    setMinPrice(record?.data.minPrice);
    setMaxPrice(record?.data.maxPrice);
    setMinUpload(record?.data.minUpload);
    setMaxUpload(record?.data.maxUpload);
  }, [
    record?.data.squareImageUrl,
    record?.data.bannerImageUrl,
    record?.data.contributorPricing,
    record?.data.minPrice,
    record?.data.maxPrice,
    record?.data.minUpload,
    record?.data.maxUpload,
  ]);

  const handleChangeSquare: UploadProps["onChange"] = ({
    fileList: newFileList,
  }) => setFileListSquare(newFileList);

  const handleChangeBanner: UploadProps["onChange"] = ({
    fileList: newFileList,
  }) => setFileListBanner(newFileList);

  const [userSelect, setUserSelect] = useState<string[]>([]);

  useEffect(() => {
    if (record?.data?.contributors?.length > 0) {
      const contributorIds = (record?.data?.contributors || []).map(
        (user: IUser) => user._id
      );
      setUserSelect(contributorIds);
    }
  }, [record?.data?.contributors]);

  const contributorProp = useMemo(() => {
    let userTable = (tableProps?.dataSource || []).filter((user: any) =>
      userSelect.includes(user._id)
    );
    return {
      dataSource: userTable,
    };
  }, [userSelect, tableProps]);

  const LabelContributor = useCallback(() => {
    return (
      <Table {...contributorProp} rowKey="_id">
        <Table.Column
          dataIndex="avatar"
          key="avatar"
          title="Avatar"
          render={(value) => <Image width={50} src={value} />}
        />
        <Table.Column
          dataIndex="username"
          key="username"
          title="Username"
          render={(_, record: any) => (
            <Link target="_blank" to={`/users/edit/${record?._id}`}>
              {record?.username}
            </Link>
          )}
        />
        <Table.Column
          dataIndex="email"
          key="email"
          title="Email"
          render={(value) => <TextField value={value} />}
        />
        <Table.Column
          dataIndex="paypalEmail"
          key="paypalEmail"
          title="Paypal Email"
          render={(value) => <TextField value={value} />}
        />
      </Table>
    );
  }, [Link, contributorProp]);

  const AccountingPeriods = useCallback(() => {
    return (
      <PayoutsOverviewList
        labelId={record?.data?._id}
        labelName={record?.data?.name}
      />
    );
  }, [record?.data?._id, record?.data?.name]);

  const AccountingPeriodsPrintout = useCallback(() => {
    return (
      <PayoutsOverviewListPrintOut
        labelId={record?.data?._id}
        tabIndex={tabIndex}
        labelName={record?.data?.name}
      />
    );
  }, [record?.data?._id, record?.data?.name, tabIndex]);

  const dataTab = useMemo(
    () => [
      {
        key: "1",
        tab: "Label Contributors",
        components: LabelContributor(),
      },
      {
        key: "2",
        tab: "Accounting Periods",
        components: AccountingPeriods(),
      },
      {
        key: "3",
        tab: "Accounting Periods Printout",
        components: AccountingPeriodsPrintout(),
      },
    ],
    [AccountingPeriods, LabelContributor, AccountingPeriodsPrintout]
  );

  const handleChangeContributorPricing = () => {
    setIsContributorPricing((pre) => !pre);
  };

  const renderListTabs = useCallback(() => {
    return dataTab.map((data) => (
      <TabPane tab={data.tab} key={data.key}>
        {data.components}
      </TabPane>
    ));
  }, [TabPane, dataTab]);

  const onChange = useCallback((tab) => {
    setTabIndex(tab);
  }, []);

  return (
      <Edit saveButtonProps={saveButtonProps} isLoading={isLoading || formLoading}>
          <div style={{ display: 'flex', height: '100%' }}>
              <Form {...formProps} layout='vertical' style={{ width: '100%' }}>
                  <Form.Item
                      label='Order'
                      name='order'
                      rules={[
                          {
                              required: false,
                          },
                      ]}
                  >
                      <Input defaultValue={record?.data.order || 1} type='number' />
                  </Form.Item>
                  <Form.Item
                      label='Name'
                      name='name'
                      rules={[
                          {
                              required: false,
                          },
                      ]}
                  >
                      <Input defaultValue={record?.data.name} />
                  </Form.Item>
                  <Form.Item
                      label='Biography'
                      name='biography'
                      rules={[
                          {
                              required: false,
                          },
                      ]}
                  >
                      <Input defaultValue={record?.data.biography} />
                  </Form.Item>

                  <div className='edit-title'>
                      <Form.Item
                          className='edit-item w48'
                          label='Contributor Pricing'
                          name='contributorPricing'
                          rules={[
                              {
                                  required: false,
                              },
                          ]}
                      >
                          <Switch defaultChecked={record?.data.contributorPricing} onChange={handleChangeContributorPricing} />
                      </Form.Item>
                      <Form.Item
                          className='edit-item w48'
                          label='IsActive'
                          name='isActive'
                          rules={[
                              {
                                  required: false,
                              },
                          ]}
                      >
                          <Switch defaultChecked={record?.data.isActive} />
                      </Form.Item>
                      <Form.Item
                          className='edit-item w48'
                          label='Label Default'
                          name='isDefault'
                          rules={[
                              {
                                  required: false,
                              },
                          ]}
                      >
                          <Switch defaultChecked={record?.data?.isDefault} />
                      </Form.Item>
                  </div>
                  {!isContributorPricing ? (
                      <div>
                          <Form.Item
                              className='edit-item w48'
                              label='Default Track Price'
                              name='defaultTrackPrice'
                              rules={[
                                  {
                                      required: false,
                                  },
                              ]}
                          >
                              <Input defaultValue={record?.data.defaultTrackPrice} type='number' />
                          </Form.Item>
                      </div>
                  ) : (
                      <div>
                          <div className='edit-title'>
                              <Form.Item
                                  className='edit-item w48'
                                  label='Min Price'
                                  name='minPrice'
                                  rules={[
                                      {
                                          required: false,
                                      },
                                      {
                                          min: 0,
                                          type: 'number',
                                          message: 'Min Price must be more than 0',
                                      },
                                      {
                                          max: maxPrice - 1,
                                          type: 'number',
                                          message: 'Min Price must be less than Max Price',
                                      },
                                  ]}
                              >
                                  <InputNumber defaultValue={record?.data.minPrice} type='number' onChange={(value: number) => setMinPrice(value)} />
                              </Form.Item>
                              <Form.Item
                                  className='edit-item w48'
                                  label='Max Price'
                                  name='maxPrice'
                                  rules={[
                                      {
                                          required: false,
                                      },
                                      {
                                          min: 0,
                                          type: 'number',
                                          message: 'Max Price must be more than 0',
                                      },
                                      {
                                          min: minPrice + 1,
                                          type: 'number',
                                          message: 'Max Price must be more than Min Price',
                                      },
                                  ]}
                              >
                                  <InputNumber defaultValue={record?.data.maxPrice} type='number' onChange={(value: number) => setMaxPrice(value)} />
                              </Form.Item>
                          </div>
                      </div>
                  )}
                  <div className='edit-title'>
                      <Form.Item
                          className='edit-item w48'
                          label='Upload Min'
                          name='minUpload'
                          rules={[
                              {
                                  required: false,
                              },
                              {
                                  min: 0,
                                  type: 'number',
                                  message: 'Min Upload must be more than 0',
                              },
                              {
                                  max: maxUpload - 1,
                                  type: 'number',
                                  message: 'Min Upload must be less than Max Upload',
                              },
                          ]}
                      >
                          <InputNumber defaultValue={record?.data.minUpload} type='number' onChange={(value: number) => setMinUpload(value)} />
                      </Form.Item>
                      <Form.Item
                          className='edit-item w48'
                          label='Upload Max'
                          name='maxUpload'
                          rules={[
                              {
                                  required: false,
                              },
                              {
                                  min: 0,
                                  type: 'number',
                                  message: 'Max Upload must be more than 0',
                              },
                              {
                                  min: minUpload + 1,
                                  type: 'number',
                                  message: 'Max Upload must be more than Min Upload',
                              },
                          ]}
                      >
                          <InputNumber defaultValue={record?.data.maxUpload} onChange={(value: number) => setMaxUpload(value)} />
                      </Form.Item>
                  </div>

                  <div className='edit-title'>
                      <Form.Item
                          className='edit-item w48'
                          label='Download Attempts On Purchases'
                          name='numberDownloads'
                          rules={[
                              {
                                  required: false,
                              },
                          ]}
                      >
                          <InputNumber defaultValue={record?.data.numberDownloads} />
                      </Form.Item>
                      <Form.Item className='edit-item w48' label='Default Split (%)' name='percentageOfTokensCanKeep'>
                          <InputNumber defaultValue={record?.data.percentageOfTokensCanKeep} min={0} max={100} step={1} />
                      </Form.Item>
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Form.Item
                          className='displayBlock w48'
                          label='Square Image'
                          name='squareImageUrl'
                          rules={[
                              {
                                  required: false,
                              },
                          ]}
                      >
                          <Upload
                              accept='image/*'
                              name='squareImageUrl'
                              listType='picture-card'
                              fileList={fileListSquare}
                              beforeUpload={beforeUpload}
                              onChange={handleChangeSquare}
                              maxCount={1}
                          >
                              change
                          </Upload>
                      </Form.Item>
                      <Form.Item
                          className='displayBlock w48'
                          label='Banner Image'
                          name='bannerImageUrl'
                          rules={[
                              {
                                  required: false,
                              },
                          ]}
                      >
                          <Upload
                              accept='image/*'
                              name='squareImageUrl'
                              listType='picture-card'
                              fileList={fileListBanner}
                              beforeUpload={beforeUpload}
                              onChange={handleChangeBanner}
                              maxCount={1}
                          >
                              change
                          </Upload>
                      </Form.Item>
                  </div>
                  <Form.Item
                      className='edit-item w48'
                      label='Label bg color'
                      name='labelBgColor'
                      rules={[
                          {
                              required: false,
                          },
                      ]}
                  >
                      <Input style={{ width: '100px' }} defaultValue={record?.data.labelBgColor} />
                  </Form.Item>
                  <Form.Item
                      label='Contributors'
                      name='contributors'
                      rules={[
                          {
                              required: false,
                          },
                      ]}
                  >
                      <Select
                          mode='multiple'
                          {...UserSelectProps}
                          defaultValue={record?.data?.contributors?.map((contributor: IUser) => contributor._id)}
                          onChange={(value) => {
                              searchFormProps?.form?.setFields(value);
                              searchFormProps?.form?.submit();
                              setUserSelect(value);
                          }}
                      />
                  </Form.Item>
              </Form>
          </div>
          <Tabs onChange={onChange} defaultActiveKey='1'>
              {renderListTabs()}
          </Tabs>
      </Edit>
  );
};
